/**
 * Finance WebSocket wire-shape constants for the dispatcher-UI badge
 * surface. Backend gateway (`backend-tms/src/finance/gateways/finance.gateway.ts`)
 * emits these events to the per-tenant `tenant:{id}:finance` room and
 * the frontend hook (`apps/web-tms/src/hooks/use-finance-badges.ts`)
 * subscribes to them — both sides import from this file so a rename
 * touches one place.
 *
 * Issue #421.
 */

/**
 * Room-scope tag passed to `TenantRoomManager` when stamping the
 * tenant room name (`tenant:{id}:finance`). Mirrors `AGENT_WS_ROOM_SCOPE`
 * precedent — having this hoisted lets gateway, integration tests, and
 * any operator dashboards that introspect socket.io rooms agree on the
 * scope identifier.
 */
export const FINANCE_WS_ROOM_SCOPE = 'finance' as const;

/**
 * Wire event names emitted on the `finance` WebSocket namespace.
 * - `INVOICES_COUNT` / `PAYMENTS_COUNT` / `EXPENSES_COUNT` are sent
 *   once per connection (initial badge snapshot).
 * - `INVOICE_STATUS_CHANGED` / `PAYMENT_STATUS_CHANGED` /
 *   `EXPENSE_STATUS_CHANGED` are deltas relayed from internal
 *   EventEmitter `*.status.changed` listeners; the frontend hook
 *   increments/decrements badge counts based on the wire status value.
 * - `ERROR` is gateway-initiated only (handshake JWT verification fail).
 */
export const FINANCE_WS_EVENTS = {
  INVOICES_COUNT: 'invoices:count',
  PAYMENTS_COUNT: 'payments:count',
  EXPENSES_COUNT: 'expenses:count',
  INVOICE_STATUS_CHANGED: 'invoice:status-changed',
  PAYMENT_STATUS_CHANGED: 'payment:status-changed',
  EXPENSE_STATUS_CHANGED: 'expense:status-changed',
  ERROR: 'error',
} as const;

export type FinanceWsEventName = (typeof FINANCE_WS_EVENTS)[keyof typeof FINANCE_WS_EVENTS];

/**
 * Status strings the gateway relays on the wire. Decoupled from
 * backend enum casing (`InvoiceStatus.PAID = 'PAID'` etc. on the
 * server) — gateway lowercases on emit, plus the
 * `ExpenseStatus.SUBMITTED → 'pending'` semantic mapping.
 *
 * Frontend hook checks against these values; defining them here pins
 * the contract instead of duplicating literals across both sides.
 */
export const FINANCE_WIRE_STATUSES = {
  /** invoice has been sent to the customer (badge increment trigger) */
  INVOICE_SENT: 'sent',
  /** invoice fully paid (badge decrement trigger) */
  INVOICE_PAID: 'paid',
  /** payment in flight or awaiting clearance */
  PAYMENT_PENDING: 'pending',
  /** payment cleared end-to-end */
  PAYMENT_COMPLETED: 'completed',
  /** expense awaiting approval (mapped from `ExpenseStatus.SUBMITTED`) */
  EXPENSE_PENDING: 'pending',
  /** expense approved by manager */
  EXPENSE_APPROVED: 'approved',
  /** expense rejected by manager */
  EXPENSE_REJECTED: 'rejected',
} as const;

/**
 * Status delta payload shape for the three `*:status-changed` events.
 * `previousStatus` is optional because some emit paths (e.g. brand-new
 * expense without prior status) don't carry a "from" value.
 */
export interface FinanceStatusChangedPayload {
  readonly previousStatus?: string;
  readonly status: string;
}

export interface InvoiceStatusChangedPayload extends FinanceStatusChangedPayload {
  readonly invoiceId: string;
}

export interface PaymentStatusChangedPayload extends FinanceStatusChangedPayload {
  readonly paymentId: string;
}

export interface ExpenseStatusChangedPayload extends FinanceStatusChangedPayload {
  readonly expenseId: string;
}
